import React from "react";
//#region types
import { WintSuperkollAPIModelFinancialReport } from "wint-api-client/dist/types";
import { CommonEnums } from "models/enums";
import { MonthPickerQuickSelection } from "models/propTypes/datepickerPropTypes";
import { DateRange } from "models/formTypes/util";
import { AccountingFormTypes } from "models/formTypes";
//#endregion
//#region components
import { useFilterContext } from "components/common";
import useAccountingDateRanges from "./useDateRanges";
//#endregion
import { DateHelper } from "utils/helpers";

function useStandardReportHelpers(
  getReport: (
    filter: WintSuperkollAPIModelFinancialReport.FinancialReportFilter
  ) => Promise<WintSuperkollAPIModelFinancialReport.FinancialReport>
) {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [result, setResult] = React.useState<WintSuperkollAPIModelFinancialReport.FinancialReportRow[]>([]);
  const [filter, setFilter] = React.useState<AccountingFormTypes.FinancialReportDateFilter>();
  const [primaryColumnQuickSelections, setPrimaryColumnQuickSelections] = React.useState<MonthPickerQuickSelection[]>(
    []
  );
  const [comparisonColumnQuickSelections, setComparisonColumnQuickSelections] = React.useState<
    MonthPickerQuickSelection[]
  >([]);

  const { filter: pageFilter, setFilter: setPageFilter } =
    useFilterContext<AccountingFormTypes.FinancialReportFilter>();

  const getMonthPickerQuickSelections = useAccountingDateRanges();

  React.useEffect(() => {
    const selections = getMonthPickerQuickSelections();
    const primaryRange = getDefaultPrimarySelection(selections.primaryColumnQuickSelections)?.range as DateRange;

    setPageFilter({ monthRange: primaryRange }, true);
  }, []);

  React.useEffect(() => {
    let columns = [] as AccountingFormTypes.FinancialReportColumnFilter[];
    let quickSelections = comparisonColumnQuickSelections;

    if (!filter) {
      const selections = getMonthPickerQuickSelections();
      const primaryColumnQuickSelections = selections.primaryColumnQuickSelections;
      setPrimaryColumnQuickSelections(primaryColumnQuickSelections);

      const defaultSelection = getDefaultPrimarySelection(primaryColumnQuickSelections);

      quickSelections = selections.comparisonColumnQuickSelections;
      setComparisonColumnQuickSelections(quickSelections);

      const comparableSelection = getDefaultComparableSelection(quickSelections);

      const primaryRange = defaultSelection?.range as DateRange;
      const secondaryRange = comparableSelection?.range
        ? typeof comparableSelection.range === "function" && primaryRange
          ? comparableSelection.range(primaryRange)
          : (comparableSelection?.range as DateRange)
        : undefined;

      columns = [{ range: primaryRange, selection: defaultSelection }];

      if (!pageFilter?.comparisonColumns) {
        columns.push({ range: secondaryRange, selection: comparableSelection });
      }
    } else {
      filter?.columns?.forEach((column) => {
        columns.push({ ...column });
      });
    }

    const pageFilterPrimaryRange =
      pageFilter?.monthRange?.start && pageFilter?.monthRange?.end ? pageFilter.monthRange : undefined;

    // Runs on every page filter change, not only the first, so that loading a saved filter also restores the
    // comparison columns.
    if (
      columns.length &&
      pageFilter?.comparisonColumns &&
      !isSameMonthRanges(getComparisonColumns(columns), pageFilter.comparisonColumns)
    ) {
      const primaryRange = pageFilterPrimaryRange ?? columns[0].range;
      columns = [
        columns[0],
        ...pageFilter.comparisonColumns.map((range) => ({
          range,
          selection: findComparisonSelection(range, primaryRange, quickSelections),
        })),
      ];
    }

    let comparisonColumnsShifted = false;

    if (columns?.length) {
      if (pageFilterPrimaryRange) {
        const primaryRangeChanged = !columns[0].range || !isSameMonthRange(columns[0].range, pageFilterPrimaryRange);
        columns[0].range = pageFilterPrimaryRange;

        if (
          columns[0].selection?.range &&
          typeof columns[0].selection?.range !== "function" &&
          !DateHelper.isRangeEqual(columns[0].range, columns[0].selection.range)
        ) {
          columns[0].selection = undefined;
        }

        // Shift on every primary change, not only while the primary keeps a quick selection: once that
        // selection is cleared, linked columns would otherwise stop following later drawer changes.
        if (primaryRangeChanged) {
          const comparisonColumnsBefore = getComparisonColumns(columns);
          updateSecondaryColumns(columns, columns[0].range);
          comparisonColumnsShifted = !isSameMonthRanges(comparisonColumnsBefore, getComparisonColumns(columns));
        }
      }
    }

    const updatedFilter = { ...filter, columns: columns, dimensions: pageFilter?.dimensions };
    if (JSON.stringify(filter) !== JSON.stringify(updatedFilter)) setFilter(updatedFilter);

    // A page filter without comparison columns (a saved filter from before they were persisted, or a cleared
    // filter) keeps the on-screen columns, so write them back to keep the URL in step with the screen.
    const comparisonColumnsMissing = !!filter && pageFilter?.comparisonColumns === undefined;

    if (comparisonColumnsShifted || comparisonColumnsMissing) {
      setPageFilter({ ...pageFilter, comparisonColumns: getComparisonColumns(columns) });
    }
  }, [pageFilter]);

  React.useEffect(() => {
    if (filter) {
      const apiFilter = { dimensions: filter.dimensions } as WintSuperkollAPIModelFinancialReport.FinancialReportFilter;

      if (filter!.columns?.length) {
        const columns = filter?.columns?.map((column) => {
          return {
            startMonth: column.range?.start ? DateHelper.getYearMonthByDate(column.range.start) : undefined,
            endMonth: column.range?.end ? DateHelper.getYearMonthByDate(column.range.end) : undefined,
          };
        });

        apiFilter.columns = columns;
      }

      setLoading(true);

      getReport(apiFilter)
        .then((res) => setResult(res.rows ?? []))
        .finally(() => setLoading(false));
    }
  }, [filter]);

  const getDefaultPrimarySelection = (primaryColumnQuickSelections: MonthPickerQuickSelection[]) => {
    const defaultSelection =
      primaryColumnQuickSelections.find((q) => q.key === CommonEnums.AccountingDateTypes.FinancialYear) ??
      primaryColumnQuickSelections.find((q) => q.key === CommonEnums.AccountingDateTypes.LastYear);

    return defaultSelection;
  };

  const getDefaultComparableSelection = (comparisonColumnQuickSelections: MonthPickerQuickSelection[]) => {
    const comparableSelection = comparisonColumnQuickSelections.find(
      (q) => q.key === CommonEnums.AccountingDateTypes.Prior12Months
    );

    return comparableSelection;
  };

  const getMonthKey = (date?: Date) => (date ? DateHelper.getYearMonth(date) : undefined);

  const isSameMonthRange = (range: DateRange, rangeToCompare: DateRange) =>
    getMonthKey(range.start) === getMonthKey(rangeToCompare.start) &&
    getMonthKey(range.end) === getMonthKey(rangeToCompare.end);

  const isSameMonthRanges = (ranges: DateRange[], rangesToCompare: DateRange[]) =>
    ranges.length === rangesToCompare.length && ranges.every((range, i) => isSameMonthRange(range, rangesToCompare[i]));

  // Only the resolved range is persisted, so the quick selection is re-derived from it. A linked selection
  // wins over a fixed one, so the column keeps following the primary period after a reload.
  const findComparisonSelection = (
    range: DateRange,
    primaryRange: DateRange | undefined,
    quickSelections: MonthPickerQuickSelection[]
  ) =>
    quickSelections.find(
      (q) => typeof q.range === "function" && primaryRange && isSameMonthRange(q.range(primaryRange), range)
    ) ?? quickSelections.find((q) => typeof q.range !== "function" && isSameMonthRange(q.range, range));

  const getComparisonColumns = (columns: AccountingFormTypes.FinancialReportColumnFilter[]) =>
    columns
      .slice(1)
      .filter((c) => c.range?.start && c.range?.end)
      .map((c) => c.range as DateRange);

  const onRepeatColumnFilterChange = (repeatIndex: number, selection: MonthPickerQuickSelection) => {
    if (!filter?.columns?.length) {
      return;
    }

    const columns = [...filter.columns];

    if (typeof selection.range === "function") {
      const primaryDateRange = filter!.columns[0];

      if (primaryDateRange.range) {
        const secondaryRange = selection.range(primaryDateRange.range);

        if (secondaryRange.start && secondaryRange.end) {
          columns[repeatIndex] = {
            range: secondaryRange,
            selection: selection,
          };
          setFilter({ ...filter, columns: columns });
          setPageFilter({ ...pageFilter, comparisonColumns: getComparisonColumns(columns) });
        }
      }
    } else {
      const range = selection.range as DateRange;

      columns[repeatIndex] = {
        range: range,
        selection: selection,
      };

      if (repeatIndex === 0) {
        updateSecondaryColumns(columns, range);

        setFilter({ ...filter, columns: columns });
        setPageFilter({ ...pageFilter, monthRange: range, comparisonColumns: getComparisonColumns(columns) });
      } else {
        setFilter({ ...filter, columns: columns });
        setPageFilter({ ...pageFilter, comparisonColumns: getComparisonColumns(columns) });
      }
    }
  };

  const onRepeatColumnDelete = (repeatIndex: number) => {
    if (filter?.columns && repeatIndex > 0) {
      const columns = [...filter.columns];
      columns.splice(repeatIndex, 1);

      setFilter({ ...filter, columns: columns });
      setPageFilter({ ...pageFilter, comparisonColumns: getComparisonColumns(columns) });
    }
  };

  const getRange = React.useCallback(
    (repeatIndex: number) => {
      if (filter?.columns && filter.columns.length > repeatIndex) {
        return filter.columns[repeatIndex].range;
      }
      return undefined;
    },
    [filter]
  );

  const getSelection = React.useCallback(
    (repeatIndex: number) => {
      if (filter?.columns && filter.columns.length > repeatIndex) {
        const column = filter.columns[repeatIndex];
        return column.selection;
      }
    },
    [filter]
  );

  const updateSecondaryColumns = (
    columns: AccountingFormTypes.FinancialReportColumnFilter[],
    primaryRange: DateRange
  ) => {
    if (columns.length > 1) {
      columns.forEach((column, index) => {
        if (index === 0) return;
        if (column.selection?.range && typeof column.selection.range === "function" && primaryRange) {
          const newDateRange = column.selection.range(primaryRange);
          column.range = newDateRange;
        }
      });
    }

    return columns;
  };

  return {
    result,
    filter,
    primaryColumnQuickSelections,
    comparisonColumnQuickSelections,
    onRepeatColumnFilterChange,
    onRepeatColumnDelete,
    getRange,
    getSelection,
    loading,
    pageFilter,
  };
}

export default useStandardReportHelpers;
